/* eslint-disable @typescript-eslint/no-shadow */
import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react'
import { routes, routes2 } from '@linqpal/models'
import numbro from 'numbro'
import { useTranslation } from 'react-i18next'
import { PayWithPaymentMethodModal } from '../components/PayWithPaymentMethodModal'
import { PayWithBankAccountFlow_ERROR } from '../components/PayWithBankAccountFlow_ERROR'
import { titleText } from '../utils/paymentModalTexts'
import {
  IntegrationErrorCodesType,
  unexpectedErrorCode,
} from '@linqpal/models/src/dictionaries'
import { handleError } from '../helpers/getErrorMetadata'
import { PayWithBankAccountFlow_PROCESSING } from '../components/PayWithBankAccountFlow_PROCESSING'
import { PricingPackageResponse } from '@linqpal/models/src/routes2/invoice/types'
import { getPaymentMethodMetadata } from '../helpers/getPaymentMethodMetadata'

enum STEPS {
  IDLE,
  PROCESSING,
  ERROR,
}

interface PayWithBankAccountFlowProps {
  invoiceId: string | null
  invoiceCompanyName: string
  paymentMethod: any
  jwtToken?: string
  invoice?: any
  onSuccess: () => void
  onClose: () => void
  onFail: () => void
  handleChangePaymentMethod?: () => void
}
interface State {
  fee: number
  totalAmount: string
  invoiceAmount: number
  lateFee: number
}

export const PayWithBankAccountFlow: React.FC<PayWithBankAccountFlowProps> = ({
  invoiceId,
  invoiceCompanyName,
  paymentMethod,
  jwtToken,
  invoice,
  onSuccess,
  onClose,
  onFail,
  handleChangePaymentMethod,
}) => {
  const [step, setStep] = useState<STEPS>(STEPS.IDLE)
  const [loading, setLoading] = useState<boolean>(false)
  const [errorCode, setErrorCode] = useState<null | IntegrationErrorCodesType>(
    null,
  )
  const [state, setState] = useState<State>()
  const [totalAmount, setTotalAmount] = useState<number>(0)

  const [status, setStatus] = useState('')
  const [message, setMessage] = useState('')

  const { t } = useTranslation(['global', 'errors'])

  const errorCodeRef = useRef(errorCode)

  useEffect(() => {
    errorCodeRef.current = errorCode
  }, [errorCode])

  const getPricingPackageFee = useCallback(async () => {
    setLoading(true)
    try {
      let res: PricingPackageResponse | null = null
      if (invoiceId) {
        res = await routes2.invoice.getPricingPackageFee({
          invoiceIds: [invoiceId],
          accountId: paymentMethod.id,
        })
      } else {
        if (invoice) {
          res = await routes2.invoice.getPricingPackageForIntegration({
            accountId: paymentMethod.id,
            companyId: invoice.company?.id,
            totalAmount: invoice.total_amount,
          })
        }
      }

      if (res?.pricingPackage) {
        const { totalAmount, discount, invoiceAmount, fee } = res.pricingPackage
        const lateFee = res.lateFee ?? 0
        const total = (totalAmount || 0) + lateFee - (discount || 0)

        setTotalAmount(total)
        setState({
          ...res.pricingPackage,
          fee: fee ?? 0,
          lateFee,
          invoiceAmount: invoiceAmount ?? 0,
          totalAmount: `$${numbro(total).format({
            thousandSeparated: true,
            mantissa: 2,
          })}`,
        })
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [invoiceId, paymentMethod, invoice])

  useEffect(() => {
    getPricingPackageFee()
  }, [getPricingPackageFee])

  const handleNext = useCallback(async () => {
    if (step === STEPS.IDLE) {
      setLoading(true)
      try {
        let invoiceIdForPay = invoiceId

        if (!invoiceIdForPay) {
          if (!jwtToken) {
            setErrorCode(unexpectedErrorCode)
            throw new Error('JWT token is missing')
          }
          const { invoiceId: newInvoiceId, errorCode: error } =
            await routes.integration.upsertInvoiceFromJwt(jwtToken)

          if (error) {
            setErrorCode(error)
            throw new Error('Error occurred while upserting invoice')
          }
          invoiceIdForPay = newInvoiceId
        }

        if (!invoiceIdForPay) {
          setErrorCode(unexpectedErrorCode)
          throw new Error('Invoice ID is missing')
        }

        await routes2.invoice.pay({
          invoiceIds: [invoiceIdForPay],
          paymentMethod: 'ach',
          accountId: paymentMethod.id,
          account_id: paymentMethod.id,
        })

        setStep(STEPS.PROCESSING)
      } catch (err: any) {
        console.error(err)

        const { status, message } = handleError(errorCodeRef.current, err, t)

        setStatus(status)
        setMessage(message)

        setStep(STEPS.ERROR)
      }
      setLoading(false)
    }
  }, [step, invoiceId, paymentMethod, t, jwtToken])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const { name: paymentMethodName, subtitle: paymentMethodSubtitle } = useMemo(
    () => getPaymentMethodMetadata(paymentMethod, t),
    [paymentMethod, t],
  )

  return (
    <>
      {step === STEPS.IDLE && (
        <PayWithPaymentMethodModal
          loading={loading}
          invoiceTotal={state}
          invoiceCompanyName={invoiceCompanyName}
          onPay={handleNext}
          onClose={handleClose}
          paymentMethod={paymentMethod}
          handleChangePaymentMethod={handleChangePaymentMethod}
        />
      )}
      {step === STEPS.PROCESSING && (
        <PayWithBankAccountFlow_PROCESSING
          invoiceTotalAmount={totalAmount}
          invoiceCompanyName={invoiceCompanyName}
          paymentMethodName={paymentMethodName}
          subtitle={paymentMethodSubtitle}
          onClose={() => {
            handleClose()
            onSuccess?.()
          }}
        />
      )}
      {step === STEPS.ERROR && (
        <PayWithBankAccountFlow_ERROR
          title={errorCode ? status : titleText(status)}
          subtitle={message}
          paymentMethodSubtitle={paymentMethodSubtitle}
          paymentMethodName={paymentMethodName}
          onClose={() => {
            handleClose()
            if (onFail) {
              onFail()
            }
          }}
          onContinue={handleClose}
        />
      )}
    </>
  )
}
